"use client";
import { Suspense, useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import AppLayout from '@/components/layout/AppLayout';
import Link from 'next/link';
import Image from 'next/image';
import { BlogPageSchema } from '@/components/seo/SchemaMarkup';
import SEOLinks from '@/components/seo/SEOLinks';
import { blogPostsData } from '../../SharedBlogData';
import { BlogPost } from '../../SharedBlogData';
import { NewsletterForm } from '@/components/NewsletterForm';
import { format, parse } from 'date-fns';
import siteConfig from '@/config/siteConfig';

// Category display name map for consistency
const categoryDisplayName: { [key: string]: string } = {
  ai: 'AI & Technology',
  analytics: 'Analytics & Insights',
  commerce: 'E-commerce & Marketplaces',
  'customer-experience': 'Customer Experience & Loyalty',
  entrepreneurship: 'Entrepreneurship',
  growth: 'Growth & Case Studies',
  policy: 'Global Policy'
};

// Get unique categories from blog posts data
const getUniqueCategories = () => {
  const categories = [...new Set(blogPostsData.map(post => post.category))];
  return categories.sort();
};

// Loading fallback component that matches the existing design
function AllBlogPostsLoading() {
  return (
    <>
      <meta name="author" content={siteConfig.authors} />
      <meta property="article:published_time" content={siteConfig.publishedTime} />
      <meta property="fb:app_id" content={siteConfig.FacebookAppID} />
      <BlogPageSchema />
      <SEOLinks customPath="/blog/all-posts" />
      <AppLayout>
        {/* Hero Section */}
        <section className="gradient-bg text-white">
          <div className="container py-16 md:py-24">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-white mb-6">
                All Blog Posts
              </h1>
              <p className="text-lg mb-8 text-gray-100">
                Explore our collection of insights, trends, and strategic guidance for building exceptional commerce experiences and capabilities.
              </p>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="bg-white border-b border-gray-200">
          <div className="container py-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <span className="font-medium">Filter by:</span>
                <div className="relative">
                  <select 
                    className="appearance-none bg-gray-50 border border-gray-300 rounded-md py-2 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-[#00A4BD]"
                    disabled
                  >
                    <option value="">All Categories</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                <div className="relative">
                  <select 
                    className="appearance-none bg-gray-50 border border-gray-300 rounded-md py-2 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-[#00A4BD]"
                    disabled
                  >
                    <option value="">All Time</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  className="bg-gray-50 border border-gray-300 rounded-md py-2 pl-10 pr-4 w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-[#00A4BD]"
                  disabled
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Loading Section */}
        <section className="section bg-gray-50">
          <div className="container">
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A4BD] mb-4"></div>
              <p className="text-gray-600">Loading blog posts...</p>
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="section bg-white">
          <div className="container">
            <NewsletterForm />
          </div>
        </section>
      </AppLayout>
    </>
  );
}

// Main content component that uses useSearchParams
function AllBlogPostsContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // Get URL parameters
  const pageParam = searchParams.get('page');
  const categoryParam = searchParams.get('category');
  const timeParam = searchParams.get('time');
  const searchParam = searchParams.get('search');
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(blogPostsData);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>(categoryParam || '');
  const [timeFilter, setTimeFilter] = useState<string>(timeParam || '');
  const [searchQuery, setSearchQuery] = useState<string>(searchParam || '');
  const [currentPage, setCurrentPage] = useState<number>(parseInt(pageParam || '1', 10));
  const postsPerPage = 9;

  useEffect(() => {
    let filtered = [...blogPosts];

    // Filter by category
    if (categoryFilter) {
      filtered = filtered.filter(post => post.category === categoryFilter);
    }

    // Filter by time
    if (timeFilter) {
      const now = new Date();
      filtered = filtered.filter(post => {
        const postDate = new Date(post.date);
        if (timeFilter === 'last-month') {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(now.getMonth() - 1);
          return postDate >= oneMonthAgo;
        } else if (timeFilter === 'last-3-months') {
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(now.getMonth() - 3);
          return postDate >= threeMonthsAgo;
        } else if (timeFilter === 'last-6-months') {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(now.getMonth() - 6);
          return postDate >= sixMonthsAgo;
        } else if (timeFilter === 'last-year') {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(now.getFullYear() - 1);
          return postDate >= oneYearAgo;
        }
        return true;
      });
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => parse(b.date, 'yyyy-MM-dd', new Date()).getTime() - parse(a.date, 'yyyy-MM-dd', new Date()).getTime());

    setFilteredPosts(filtered);
    
    // Reset to page 1 when filters change (but not on initial load)
    if (currentPage > 1 && filtered.length <= postsPerPage) {
      setCurrentPage(1);
    }
  }, [categoryFilter, timeFilter, searchQuery, blogPosts, currentPage, postsPerPage]);

  // Update URL when filters or page change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (categoryFilter) params.set('category', categoryFilter);
    if (timeFilter) params.set('time', timeFilter);
    if (searchQuery) params.set('search', searchQuery);
    
    const newUrl = params.toString() ? `/blog/all-posts?${params.toString()}` : '/blog/all-posts';
    
    // Use replace to avoid adding to history for every filter change
    router.replace(newUrl, { scroll: false });
  }, [currentPage, categoryFilter, timeFilter, searchQuery, router]);

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Filter change handlers that reset to page 1
  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };

  const handleTimeChange = (value: string) => {
    setTimeFilter(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // Get unique categories for the filter dropdown
  const uniqueCategories = getUniqueCategories();

  return (
    <>
      <BlogPageSchema />
      <SEOLinks customPath="/blog/all-posts" />
      <AppLayout>
        {/* Hero Section */}
        <section className="gradient-bg text-white">
          <div className="container py-16 md:py-24">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-white mb-6">
                All Blog Posts
              </h1>
              <p className="text-lg mb-8 text-gray-100">
                Explore our collection of insights, trends, and strategic guidance for building exceptional commerce experiences and capabilities.
              </p>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="bg-white border-b border-gray-200">
          <div className="container py-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <span className="font-medium">Filter by:</span>
                <div className="relative">
                  <select 
                    className="appearance-none bg-gray-50 border border-gray-300 rounded-md py-2 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-[#00A4BD]"
                    value={categoryFilter}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {uniqueCategories.map(category => (
                      <option key={category} value={category}>
                        {categoryDisplayName[category] || category}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                <div className="relative">
                  <select 
                    className="appearance-none bg-gray-50 border border-gray-300 rounded-md py-2 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-[#00A4BD]"
                    value={timeFilter}
                    onChange={(e) => handleTimeChange(e.target.value)}
                  >
                    <option value="">All Time</option>
                    <option value="last-month">Last Month</option>
                    <option value="last-3-months">Last 3 Months</option>
                    <option value="last-6-months">Last 6 Months</option>
                    <option value="last-year">Last Year</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  className="bg-gray-50 border border-gray-300 rounded-md py-2 pl-10 pr-4 w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-[#00A4BD]"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* All Posts Section */}
        <section className="section bg-gray-50">
          <div className="container">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentPosts.map(post => (
                <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <Link href={post.customPath} className="block">
                    <Image
                      src={post.image}
                      alt={post.title}
                      width={400}
                      height={192}
                      className="w-full h-48 object-cover hover:opacity-90 transition-opacity"
                      sizes="(max-width: 1024px) 100vw, 33vw"
                    />
                  </Link>
                  <div className="p-6">
                    <div className="flex items-center mb-3">
                      <span className="bg-[#E6F7F9] text-[#00A4BD] text-xs px-2 py-1 rounded-full">
                        {categoryDisplayName[post.category] || post.category}
                      </span>
                      <span className="text-gray-500 text-xs ml-3">{format(parse(post.date, 'yyyy-MM-dd', new Date()), 'MMMM d, yyyy')}</span>
                    </div>
                    <h3 className="text-xl mb-3">
                      <Link href={post.customPath} className="hover:text-[#008CA1]">
                        {post.title}
                      </Link>
                    </h3>
                    <p className="mb-4 text-sm">{post.excerpt}</p>
                    <div className="flex items-center mb-4">
                      <Link href={`/team/${post.author.id}`} className="flex items-center hover:opacity-80 transition-opacity">
                        <Image
                          src={post.author.image}
                          alt={post.author.name}
                          width={32}
                          height={32}
                          className="w-12 h-12 object-cover rounded-full mr-3"
                        />
                        <div>
                          <p className="text-sm font-medium hover:text-[#00A4BD] transition-colors">{post.author.name}</p>
                          <p className="text-xs text-gray-600">{post.author.jobTitle}</p>
                        </div>
                      </Link>
                    </div>
                    <Link href={post.customPath} className="text-[#00A4BD] font-medium hover:text-[#008CA1] text-sm">
                      Read Article →
                    </Link>
                  </div>
                </div>
              ))}
              {currentPosts.length === 0 && (
                <div className="text-center py-12 col-span-1 md:col-span-2 lg:col-span-3">
                  <p className="text-gray-500">No posts found matching your criteria.</p>
                </div>
              )}
            </div>

            {/* Enhanced Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center mt-12 space-y-4">
                <nav className="inline-flex rounded-md shadow">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="py-2 px-4 border border-gray-300 bg-white rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`py-2 px-4 border-t border-b border-gray-300 ${
                        currentPage === number ? 'bg-[#E6F7F9] text-[#00A4BD] font-medium' : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="py-2 px-4 border border-gray-300 bg-white rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="section bg-white">
          <div className="container">
            <NewsletterForm />
          </div>
        </section>
      </AppLayout>
    </>
  );
}

// Main export with Suspense boundary
export default function AllBlogPostsPage() {
  return (
    <Suspense fallback={<AllBlogPostsLoading />}>
      <AllBlogPostsContent />
    </Suspense>
  );
}